import type { PersonalizationRow } from '@/types'

/** Server cap (StoreOrderRequest max:200) - auto rows stop here so huge orders stay placeable. */
export const MAX_PERSONALIZATION_ROWS = 200

/**
 * Reconcile the name & number rows with the size matrix: one row per piece,
 * in product size order. For each size with qty n the first n existing rows
 * of that size survive (typed names are kept), blanks pad the rest, extras
 * are dropped. Returns a new array.
 */
export function syncPersonalizations(
  rows: PersonalizationRow[],
  sizeMatrix: Record<string, number>,
  sizes: string[],
): PersonalizationRow[] {
  const next: PersonalizationRow[] = []
  for (const size of sizes) {
    const qty = Math.max(0, sizeMatrix[size] ?? 0)
    const existing = rows.filter((row) => row.size === size)
    for (let i = 0; i < qty && next.length < MAX_PERSONALIZATION_ROWS; i++) {
      next.push(existing[i] ?? { size, name: '', number: '' })
    }
  }
  return next
}

/** What people type in the NO column when a piece has no number. */
const NO_NUMBER = new Set(['no num', 'no number', 'nonum', 'no no', '-', 'none', 'tiada', 'n/a', 'na', 'x'])

/**
 * The number worth printing on a page, or null for a blank or a "no num"
 * style placeholder. Display only - the stored row keeps what was typed.
 * "0" is a real jersey number and survives.
 */
export function realNumber(value: string | null | undefined): string | null {
  const trimmed = String(value ?? '').trim()
  return trimmed === '' || NO_NUMBER.has(trimmed.toLowerCase()) ? null : trimmed
}

/** Rows still missing a name or a number - every row is compulsory once personalization is on. */
export function incompleteRows(rows: PersonalizationRow[]): PersonalizationRow[] {
  return rows.filter((row) => row.name.trim() === '' || row.number.trim() === '')
}
